/**
 * Pure fallback-chain DRIVER for the SpriteResolver — no Phaser, no network. Kept
 * separate from SpriteResolver.ts (which imports Phaser) so the SELECTION logic is
 * unit-testable in the node environment.
 */
import type { BattleTier } from './paths.js';

/** Outcome of a resolve: the Phaser texture key + which source URL/tier won. */
export interface ResolvedSprite {
  readonly key: string;
  readonly url: string;
  /** Battle-tier the winning url belongs to (telemetry / coverage display). */
  readonly tier: BattleTier;
}

/** Attempts to load one url into a texture `key`; resolves true on success. */
export type LoadAttempt = (key: string, url: string) => Promise<boolean>;

/**
 * Walk an ordered candidate list, returning the FIRST that loads. Pure given an
 * injected `attempt` — this is the fallback-chain core the tests exercise. Returns
 * null only if every candidate (incl. the placeholder) fails to load.
 */
export async function resolveFirst(
  key: string,
  candidates: readonly string[],
  attempt: LoadAttempt,
): Promise<string | null> {
  for (const url of candidates) {
    if (await attempt(key, url)) return url;
  }
  return null;
}
